import { LogOut, Moon, Settings, Sun } from "lucide-react";

import { handleSignOut } from "@/lib/auth";
import { useStore } from "@/lib/store";

interface SidebarFooterProps {
  collapsed?: boolean;
}

export function SidebarFooter({ collapsed }: SidebarFooterProps): JSX.Element | null {
  const user = useStore((state) => state.user);
  const activeView = useStore((state) => state.activeView);
  const setView = useStore((state) => state.setView);
  const closeSidebar = useStore((state) => state.closeSidebar);
  const theme = useStore((state) => state.theme);
  const setTheme = useStore((state) => state.setTheme);

  function handlePreferences(): void {
    setView("preferences");
    const url = new URL(window.location.href);
    url.searchParams.delete("thread");
    url.searchParams.delete("automation");
    url.searchParams.delete("moriarty");
    url.searchParams.delete("doc");
    url.searchParams.set("view", "preferences");
    window.history.pushState({}, "", url);
    closeSidebar();
  }

  if (!user) return null;

  // Collapsed view - just avatar
  if (collapsed) {
    return (
      <div className="flex justify-center border-t border-cyber-border p-3">
        {user.image ? (
          <img src={user.image} alt="" className="h-8 w-8 rounded-full" title={user.email} />
        ) : (
          <div
            className="flex h-8 w-8 items-center justify-center rounded-full bg-cyber-surface text-xs text-cyber-muted"
            title={user.email}
          >
            {user.email[0].toUpperCase()}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="border-t border-cyber-border px-3 py-3">
      <div className="flex items-center justify-between">
        <div className="flex min-w-0 items-center gap-2">
          {user.image ? (
            <img src={user.image} alt="" className="h-6 w-6 flex-shrink-0 rounded-full" />
          ) : (
            <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-cyber-surface text-xs text-cyber-muted">
              {user.email[0].toUpperCase()}
            </div>
          )}
          <span className="truncate text-xs text-cyber-text">{user.email.split("@")[0]}</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="inline-flex items-center rounded-full border border-cyber-border bg-cyber-bg p-0.5"
            role="tablist"
            aria-label="Theme"
          >
            <button
              type="button"
              onClick={() => setTheme("cyber")}
              className={
                theme === "cyber"
                  ? "rounded-full bg-cyber-cyan p-1.5 text-white"
                  : "rounded-full p-1.5 text-cyber-muted/60 hover:text-cyber-muted"
              }
              title="Cyber theme"
              aria-label="Cyber theme"
              role="tab"
              aria-selected={theme === "cyber"}
            >
              <Moon size={14} />
            </button>
            <button
              type="button"
              onClick={() => setTheme("base")}
              className={
                theme === "base"
                  ? "rounded-full bg-cyber-cyan p-1.5 text-white"
                  : "rounded-full p-1.5 text-cyber-muted/60 hover:text-cyber-muted"
              }
              title="Base theme"
              aria-label="Base theme"
              role="tab"
              aria-selected={theme === "base"}
            >
              <Sun size={14} />
            </button>
          </div>
          <button
            type="button"
            onClick={handlePreferences}
            className={
              activeView === "preferences"
                ? "rounded p-2 text-cyber-cyan transition-all hover:bg-cyber-surface"
                : "rounded p-2 text-cyber-muted transition-all hover:bg-cyber-surface hover:text-cyber-cyan"
            }
            title="Preferences"
            aria-label="Preferences"
          >
            <Settings size={14} />
          </button>
          <button
            onClick={handleSignOut}
            className="rounded p-2 text-cyber-muted transition-all hover:bg-cyber-surface hover:text-cyber-red"
            title="Sign out"
            aria-label="Sign out"
          >
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
